import { QuestionCircleOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Col, Input, Popconfirm, Popover, Row, Table, Tag } from "antd";
import React, { useState } from "react";
import { CiCirclePlus } from "react-icons/ci";
import { FiEdit } from "react-icons/fi";
import { VscEye, VscTrash } from "react-icons/vsc";
import { Link } from "react-router-dom";
import { deleteProject } from "../../../../../api/apiCallManager";
import AccessTo from "../../../../../components/is-show/AccessTo";
import { showSuccess } from "../../../../../helpers/alerts";
import {
  LOCAL_STORAGE_KEY,
  REMOVE_LOCAL_STORAGE_BY_KEY,
} from "../../../../../helpers/localStorage";
import { CM, FL, ML, PA, PFA, SM } from "../../../../router/AddRoutesJson";
import TableTest from "../../../../test/TableTest";
import useFLProjectList from "./../../../../../components/hooks/useFLProjectList";

function ListFLProjects() {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState(""); // Step 1: Add state for search input

  const { data: projectListFL, error, isLoading } = useFLProjectList();

  const filteredData = projectListFL?.body
    ?.filter((project) =>
      project.projectTitle.toLowerCase().includes(searchText.toLowerCase())
    )
    ?.map((project) => ({
      key: project.record_id,
      name: project.projectTitle,
      owner: project.projectOwner,
      projectStatus: project.projectStatus,
      description: project.projectDescription,
    }));
  const deleteMutation = useMutation({
    mutationFn: deleteProject,
    onSuccess: (data) => {
      if (data.statusCode == 200) {
        showSuccess("Project deleted successfully!");
        queryClient.invalidateQueries({ queryKey: ["projectListFL"] });
      }
    },
  });

  const statusColorMap = {
    "No Setup": "red",
    "Project Created": "green",
    Completed: "blue",
    "Roles Assigned": "pink",
    "Data Uploaded": "green",
    "Pending Approval": "blue",
    "Ready to Train": "green",
  };
  const columns = [
    {
      title: "Project Title",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Project Owner",
      dataIndex: "owner",
      key: "owner",
    },

    {
      title: "Project Status",
      dataIndex: "projectStatus",
      key: "projectStatus",
      filters: [
        {
          text: "Created",
          value: "Created",
        },
        {
          text: "Roles Assigned",
          value: "Roles Assigned",
        },
        {
          text: "Data Uploaded",
          value: "Data Uploaded",
        },
        // {
        //   text: "Pending Approval",
        //   value: "Pending Approval",
        // },
        // {
        //   text: "Ready to Train",
        //   value: "Ready to Train",
        // },
        // {
        //   text: "Completed",
        //   value: "Completed",
        // },
        // {
        //   text: "Freezed",
        //   value: "Freeze",
        // },
        // Add more filters as needed
      ],
      onFilter: (value, record) => record.projectStatus.includes(value),
      render: (item) => {
        // Determine the color based on the status
        const color = statusColorMap[item];
        // Return the JSX with the computed color
        return (
          <Tag color={color} key={item}>
            {item || "None"}
          </Tag>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (para) => (
        <div>
          <Popover
            overlayInnerStyle={{
              maxHeight: "300px",
              overflowY: "auto",
            }}
            overlayStyle={{
              maxWidth: "35vw",
            }}
            placement="top"
            content={<>{para}</>}
            title="Description"
          >
            {para?.substring(0, 18)}
            {para?.length > 18 ? "...." : ""}
          </Popover>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, data) => (
        <div className="flex">
          <AccessTo role={[PFA, ML, FL, CM, SM]}>
            <Link to={`/preview-fl-project/${data?.key}`} className="h-0">
              <button className="mx-2">
                <VscEye size={16} color="#4ea927" className="opacity-[0.8]" />
              </button>
            </Link>
          </AccessTo>
          <AccessTo role={[PA]}>
            <Link to={`/preview-fl-project/${data?.key}`} className="h-0">
              <button className="mx-2">
                <FiEdit size={16} color="#4ea927" className="opacity-[0.8]" />
              </button>
            </Link>
            <button>
              <Popconfirm
                title="Are you sure?"
                description="Once deleted, it cannot be recovered."
                onConfirm={() => deleteMutation.mutate(data.key)}
                icon={<QuestionCircleOutlined style={{ color: "red" }} />}
              >
                <VscTrash size={18} color="red" />
              </Popconfirm>
            </button>
          </AccessTo>
        </div>
      ),
    },
  ];

  return (
    <div className="crd-wrap">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Row gutter={[16, 16]}>
            <Col span={9}>
              <Input
                // prefix={<IoIosSearch color="grey" />}
                placeholder="Search by project name"
                value={searchText}
                allowClear
                onChange={(e) => setSearchText(e.target.value)}
                className="mb-4"
              />
            </Col>
            <Col span={9}></Col>
            <Col span={6}>
              <AccessTo role={[PA]}>
                <div className="flex justify-end">
                  <Link
                    to="/create-fl-project"
                    onClick={REMOVE_LOCAL_STORAGE_BY_KEY(
                      LOCAL_STORAGE_KEY.currentFLProjectId
                    )}
                  >
                    <button className="crt-usr-btn mb-4">
                      <CiCirclePlus size={17} className="me-1 stroke-[0.6px]" />
                      Create Project
                    </button>
                  </Link>
                </div>
              </AccessTo>
            </Col>
          </Row>

          <Table
            loading={isLoading}
            pagination={{ pageSize: 7 }}
            scroll={{ x: 600 }}
            dataSource={filteredData}
            columns={columns}
            className="user-table-theme"
          />
        </Col>

        <TableTest />
      </Row>
    </div>
  );
}

export default ListFLProjects;
